import { EditOutlined } from '@ant-design/icons';
import { t } from '@hefe/i18n';
import { Inventory, UserBasic } from '@hefe/models';
import { InventoryApi, InventorySlice, ModalsSlice } from '@hefe/store';
import { fullUserName } from '@hefe/utils';
import { Button } from 'antd';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../store/store';
import CustomTable from '../../../core/components/table/custom-table.component';
import AddEditInventoryForm from '../../forms/add-edit-inventory/add-edit-inventory.form';
import InventoryDetailsComponent from '../inventory-details/inventory-details.component';

export default function InventoryListComponent() {
  const dispatch = useAppDispatch();
  const selectedKey = useSelector(InventorySlice.selectedKeySelector);

  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);

  const categoryId = useMemo(() => {
    if (selectedKey === 'uncategorized') {
      return 0;
    }

    if (selectedKey === 'undefined') {
      return 0;
    }

    return selectedKey ? parseInt(selectedKey) : 0;
  }, [selectedKey]);

  const { data: inventories, isFetching: isFetchingInventory } =
    InventoryApi.useFetchAllInventoriesQuery(categoryId);

  const [deleteInventory, { isLoading: isDeletingInventory }] =
    InventoryApi.useDeleteInventoryMutation();

  const onInventoryClick = (inventory: Inventory) => {
    dispatch(
      ModalsSlice.modalsActions.openModal({
        title: inventory.name,
        component: (
          <InventoryDetailsComponent inventory={inventory} adminView />
        ),
        width: '90%',
        hideOkButton: true,
        hideCancelButton: true,
      })
    );
  };

  const onEditInventoryClick = () => {
    const inventory = inventories?.find((i) => i.id === selectedRowKeys[0]);

    if (!inventory) {
      return;
    }

    dispatch(
      ModalsSlice.modalsActions.openModal({
        title: t('inventory.edit'),
        formId: `add-edit-inventory-form-${inventory.id}`,
        component: <AddEditInventoryForm inventory={inventory} />,
      })
    );
  };

  const onDeleteInventory = (id: number, resetKeys?: () => void) => {
    const inventory = inventories?.find((i) => i.id === id);

    if (inventory) {
      deleteInventory(inventory)
        .unwrap()
        .then(() => {
          resetKeys?.();
        });
    }
  };

  return (
    <CustomTable
      name="inventory-list"
      rowKey="id"
      columns={[
        {
          key: 'name',
          title: 'Name',
          default: true,
          dataIndex: 'name',
          sortType: 'string',
        },
        {
          key: 'possessor',
          title: t('inventory.details.possessor'),
          default: true,
          dataIndex: 'possessor',
          render: (possessor: UserBasic) =>
            possessor ? fullUserName(possessor) : t('inventory.pool.title'),
          sortType: 'string',
        },
        {
          key: 'status',
          title: t('inventory.details.status'),
          default: true,
          dataIndex: 'has_active_transfer',
          render: (has_active_transfer: boolean) =>
            t(
              `inventory.transfers.status.${
                has_active_transfer ? 'pending' : 'transferred'
              }`
            ),
          sortType: 'string',
        },
      ]}
      dataSource={inventories ?? []}
      loading={isFetchingInventory}
      selectedRowKeys={selectedRowKeys}
      onRowSelect={(keys: number[]) => setSelectedRowKeys(keys)}
      addForm={{
        title: t('inventory.add'),
        formId: 'add-edit-inventory-form-new',
        component: <AddEditInventoryForm />,
      }}
      onRowClick={(inventory: Inventory) => onInventoryClick(inventory)}
      deleteAction={{
        action: (id: number[], resetKeys) =>
          onDeleteInventory(id[0], resetKeys),
        loading: isDeletingInventory,
      }}
      toolbarItems={[
        <Button
          disabled={selectedRowKeys.length !== 1}
          icon={<EditOutlined />}
          onClick={() => onEditInventoryClick()}
        />,
      ]}
    />
  );
}
